(function () {
  'use strict';

  // Ordinary searches stay plain text so Shopify can match and rank them.
  // Reviewed alternatives are opt-in links, never injected into the query.
  // Stock shortcuts and explicit Shopify syntax keep their existing meaning.
  const cacheLimit = 80;
  const corrections = new Map();
  const cyrillic = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', ђ: 'đ', е: 'e', ж: 'ž',
    з: 'z', и: 'i', ј: 'j', к: 'k', л: 'l', љ: 'lj', м: 'm', н: 'n',
    њ: 'nj', о: 'o', п: 'p', р: 'r', с: 's', т: 't', ћ: 'ć', у: 'u',
    ф: 'f', х: 'h', ц: 'c', ч: 'č', џ: 'dž', ш: 'š',
  };
  function normalizeQuery(value) {
    return String(value || '').normalize('NFC').trim().replace(/\s+/g, ' ');
  }
  function latin(value) {
    return String(value).toLowerCase().replace(/[а-яђјљњћџ]/g, letter => cyrillic[letter] || letter);
  }
  function fold(value) {
    return latin(value).replace(/đ/g, 'dj').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  // Reviewed forms, not stems. First entry is the optional alternative spelling.
  // Add families only after checking meaning; never infer by removing a letter.
  const families = [
    'barbie barbi',
    'montesori montessori',
    'puzle puzzle puzla puzli puzlama',
    'kartice kartica karticu kartici karticom karticama',
    'karte karta kartu karti kartom kartama',
    'pametnice pametnica pametnicu pametnici pametnicom pametnicama',
    'igračke igračka igračku igrački igračkom igračkama igračaka',
    'igre igra igru igri igrom igrama igara',
    'društvene društvena društveni društveno društvenih društvenim društvenu društvenog društvenoj',
    'drvene drvena drveni drveno drvenih drvenim drvenu drvenog drvenoj drvenom',
    'kocke kocka kocku kocki kockom kockama',
    'kockice kockica kockicu kockici kockicom kockicama',
    'lutke lutka lutku lutki lutkom lutkama lutaka',
    'guralica guralice guralicu guralici guralicom guralicama',
    'slagalica slagalice slagalicu slagalici slagalicom slagalicama',
    'lopte lopta loptu lopti loptom loptama',
    'autići autić autića autiću autićem autićima',
    'automobili automobil automobila automobilu automobilom automobilima',
    'kamioni kamion kamiona kamionu kamionom kamionima',
    'traktori traktor traktora traktoru traktorom traktorima',
    'bageri bager bagera bageru bagerom bagerima',
    'vozovi voz voza vozu vozom vozovima',
    'vozići vozić vozića voziću vozićem vozićima',
    'kolica kolicima',
    'knjige knjiga knjigu knjizi knjigom knjigama',
    'slikovnice slikovnica slikovnicu slikovnici slikovnicom slikovnicama',
    'bojanke bojanka bojanku bojanci bojankom bojankama',
    'ranac ranca rancu rancem rančevi rančeva rančevima',
    'bebe beba bebu bebi bebom bebama',
    'deca dete decu deci decom deteta detetu detetom',
    'dečije dečija dečiji dečiju dečijim dečijih dečijeg dečijoj dečijem',
    'devojčice devojčica devojčicu devojčici devojčicom devojčicama',
    'dečaci dečak dečaka dečaku dečakom dečacima',
    'edukativne edukativna edukativni edukativno edukativnu edukativnim edukativnih edukativnog edukativnoj',
    'interaktivne interaktivna interaktivni interaktivno interaktivnu interaktivnim interaktivnih interaktivnog',
    'muzičke muzička muzički muzičko muzičku muzičkim muzičkih muzičkog',
    'magnetne magnetna magnetni magnetno magnetnu magnetnim magnetnih magnetnog',
    'plišane plišana plišani plišano plišanu plišanim plišanih plišanog',
    'zvučne zvučna zvučni zvučno zvučnu zvučnim zvučnih zvučnog',
    'kuhinje kuhinja kuhinju kuhinji kuhinjom kuhinjama',
    'tabla table tablu tabli tablom tablama',
    'zvečke zvečka zvečku zvečki zvečkom zvečkama',
    'ljuljaške ljuljaška ljuljašku ljuljašci ljuljaškom ljuljaškama',
    'trotinet trotineti trotineta trotinetu trotinetom trotinetima',
    'bicikl bicikli bicikla biciklu biciklom biciklima',
    'tricikl tricikli tricikla triciklu triciklom triciklima',
    'projektori projektor projektora projektoru projektorom projektorima',
    'životinje životinja životinju životinji životinjom životinjama',
    'dinosaurusi dinosaurus dinosaurusa dinosaurusu dinosaurusom dinosaurima',
    'crvene crvena crveni crveno crvenu crvenim crvenih crvenog',
    'plave plava plavi plavo plavu plavim plavih plavog',
    'zelene zelena zeleni zeleno zelenu zelenim zelenih zelenog',
    'žute žuta žuti žuto žutu žutim žutih žutog',
    'roze roza rozi rozu',
    'beli bela belo bele belu belim belih belog',
    'crni crna crno crne crnu crnim crnih crnog',
  ].map(words => Object.freeze([...new Set(words.split(' '))]));
  const familyByKey = new Map();
  families.forEach(family => family.forEach(word => familyByKey.set(fold(word), family)));
  // Reviewed directional aliases and typos. Never infer corrections from edit
  // distance: real words such as "kofica" and "tablet" must stay intact.
  const aliases = new Map([
    ['barbi', 'barbie'],
    ['montessori', 'montesori'],
    ['puzzle', 'puzle'],
    ['drvne', 'drvene'],
    ['kocle', 'kocke'],
  ]);

  function remember(cache, key, value) {
    if (cache.size >= cacheLimit) cache.delete(cache.keys().next().value);
    cache.set(key, value);
    return value;
  }
  function isPlainQuery(value) {
    const query = normalizeQuery(value);
    return !!query && !/["\\():*]|(?:^|\s)(?:AND|OR|NOT)(?:\s|$)|(?:^|\s)-\S/.test(query);
  }
  function prefixMode(value) {
    const query = normalizeQuery(value);
    return !query || isPlainQuery(query) ? 'last' : 'none';
  }
  function skuSuffix(value) {
    return normalizeQuery(value).match(/^\*-([0-9]{2})$/)?.[1] || '';
  }
  function parseInput(value) {
    const rawQuery = normalizeQuery(value == null ? '' : String(value));
    if (!rawQuery.startsWith('%')) return { query: rawQuery, unavailableMode: 'hide', stockMode: 'available' };
    const query = rawQuery.replace(/^%+/, '').replace(/%+$/, '').trim();
    const soldOutOnly = !!query && rawQuery.endsWith('%');
    return { query, unavailableMode: soldOutOnly ? 'show' : 'last', stockMode: soldOutOnly ? 'soldout' : 'all' };
  }
  function applyStockParams(params, config) {
    params.set('options[unavailable_products]', config.stockMode === 'soldout' ? 'show' : config.unavailableMode);
    params.delete('filter.v.availability');
    if (config.stockMode === 'soldout') params.set('filter.v.availability', '0');
    return params;
  }
  function stockConfigFromParams(params) {
    const values = params.getAll('filter.v.availability').flatMap(value => value.split(',')).map(value => value.trim());
    const mode = params.get('options[unavailable_products]');
    const available = values.includes('1'), unavailable = values.includes('0');
    if (available && unavailable) return { unavailableMode: mode === 'last' ? 'last' : 'show', stockMode: 'all' };
    if (unavailable) return { unavailableMode: 'show', stockMode: 'soldout' };
    if (available) return { unavailableMode: 'hide', stockMode: 'available' };
    if (mode === 'last' || mode === 'show') return { unavailableMode: mode, stockMode: 'all' };
    return { unavailableMode: 'hide', stockMode: 'available' };
  }
  function fullQuery(value) {
    const query = normalizeQuery(value);
    if (!isPlainQuery(query)) return query;
    return query.split(' ').map(word => {
      // Preserve codes, numbers and punctuation. Only reviewed whole words can
      // regain diacritics; there is no catalog-wide prefix or synonym expansion.
      if (!/^\p{L}+$/u.test(word)) return word;
      const family = familyByKey.get(fold(word));
      const spelling = family?.find(candidate => fold(candidate) === fold(word));
      if (spelling) return spelling;
      return /[а-яђјљњћџ]/i.test(word) ? latin(word) : word;
    }).join(' ');
  }
  function canonicalWord(word) {
    return familyByKey.get(fold(word))?.[0] || word;
  }
  function predictiveQuery(value) {
    return fullQuery(value);
  }
  async function resolveQuery(value) {
    return fullQuery(value);
  }
  async function resolveSearch(value) {
    const original = normalizeQuery(value);
    const query = fullQuery(original);
    if (!isPlainQuery(original)) return { query, correction: '', correctionQuery: '' };
    if (corrections.has(original)) return corrections.get(original);
    const words = query.split(' ');
    let correction = '';
    // One opt-in alternative: a reviewed alias/typo or grammatical form.
    // The primary query is never replaced by this suggestion.
    const strategies = [
      word => aliases.get(fold(word)) || '',
      word => familyByKey.get(fold(word))?.[0] || '',
    ];
    for (const suggest of strategies) {
      for (let position = 0; position < Math.min(words.length, 8); position++) {
        const word = words[position];
        if (!/^\p{L}+$/u.test(word)) continue;
        const replacement = suggest(word);
        if (!replacement || fold(replacement) === fold(word)) continue;
        const alternative = [...words];
        alternative[position] = replacement;
        correction = alternative.join(' ');
        break;
      }
      if (correction) break;
    }
    return remember(corrections, original, {
      query, correction, correctionQuery: correction ? fullQuery(correction) : '',
    });
  }
  function scoreProduct(value, title, skus = []) {
    const query = normalizeQuery(value);
    if (!query || !isPlainQuery(query)) return 0;
    if (Array.isArray(skus) && skus.some(sku => String(sku).toLowerCase() === query.toLowerCase())) return 10000;
    const rawWords = fold(query).match(/[a-z0-9]+/g) || [];
    const titleWords = fold(title).match(/[a-z0-9]+/g) || [];
    const normalized = words => words.map(word => fold(canonicalWord(word)));
    const wanted = normalized(rawWords), named = normalized(titleWords);
    const all = wanted.length && wanted.every(word => named.includes(word));
    const phrase = ` ${titleWords.join(' ')} `.includes(` ${rawWords.join(' ')} `);
    const canonicalPhrase = ` ${named.join(' ')} `.includes(` ${wanted.join(' ')} `);
    if (phrase) return 3000;
    if (all && canonicalPhrase) return 2500;
    if (all) return 2000;
    const prefix = wanted.length && wanted.every((word, position) =>
      named.some(candidate => candidate === word ||
        (position === wanted.length - 1 && word.length >= 3 && candidate.startsWith(word))));
    return prefix ? 1000 : 0;
  }
  window.WokieeSearchQuery = Object.freeze({
    fullQuery, resolveQuery, resolveSearch, predictiveQuery, scoreProduct,
    isPlainQuery, prefixMode, parseInput, applyStockParams, stockConfigFromParams, skuSuffix,
  });
})();
